import {
  ArrowDownUp,
  ArrowLeftRight,
  ImageIcon,
  Maximize,
  SquareDashedBottom,
} from "lucide-react";
import React, { useState } from "react";

import {
  IgButton,
  IgSelect,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import type { IconPosition } from "@intelligems/ig-types";
import type { BannerComponent } from "@src/store/slices/experienceBuilder/types";
import { InputAdornment, MenuItem, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { ImageLibraryDialog } from "@src/components/Dialogs/ImageLibraryDialog";
import { RichTextEditor } from "@src/components/RichTextEditor";
import { MenuBarColorPicker } from "@src/components/RichTextEditor/MenuBar/MenuBarColorPicker";
import { URLInputDialog } from "@src/features/experienceBuilder/modifications/basicMods/URLInputDialog";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDialog } from "@src/hooks/dialog";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";

export const BannerComponentPanel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const imageLibraryDialog = useDialog("imageLibrary");
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);

  const mod = useSelector(Selector.component.getVariationDraft) as
    | BannerComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);

  const update = (changes: Partial<BannerComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  const handleSelectIcon = () => {
    imageLibraryDialog.open({
      id: imageLibraryDialog.id,
      onUpdateImage: (iconSrc: string) => update({ iconSrc }),
    });
  };

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          {/* Banner text */}
          <Stack spacing={1}>
            <Panel.Header>Banner text</Panel.Header>
            <RichTextEditor
              value={mod.content}
              onChange={(content) => update({ content })}
              disabled={false}
              placeholder="Free shipping on orders over $50"
              minHeight="4em"
            />
          </Stack>

          {/* Icon / Image */}
          <Stack spacing={1}>
            <Panel.Label>
              Icon / Image
              <Panel.Optional />
            </Panel.Label>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "8px",
                px: 2,
                py: 1.5,
              }}
            >
              {mod.iconSrc ? (
                <img
                  src={mod.iconSrc}
                  alt="Icon"
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
              ) : (
                <ImageIcon size={24} color={theme.palette.text.secondary} />
              )}
              <IgTypography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
                noWrap
              >
                {mod.iconSrc
                  ? mod.iconSrc.split("/").pop()
                  : "No icon / image selected"}
              </IgTypography>
              <IgButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleSelectIcon}
              >
                Choose
              </IgButton>
            </Stack>
          </Stack>

          {/* Icon position */}
          <IgSelect<IconPosition>
            label="Icon position"
            fullWidth
            size="small"
            value={mod.iconPosition}
            onChange={(e) => update({ iconPosition: e.target.value })}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </IgSelect>
          <Stack spacing={1}>
            <Panel.Label>Icon position</Panel.Label>
          </Stack>

          {/* Background color */}
          <Stack spacing={1}>
            <Panel.Label>Background color</Panel.Label>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MenuBarColorPicker
                value={mod.backgroundColor}
                onChange={(backgroundColor) => update({ backgroundColor })}
                disabled={false}
              />
              <Panel.TextField
                value={mod.backgroundColor}
                onChange={(e) => update({ backgroundColor: e.target.value })}
              />
            </Stack>
          </Stack>

          {/* Stroke color + Stroke width */}
          <Grid container spacing={2}>
            <Grid size={6}>
              <Stack spacing={1}>
                <Panel.Label>Stroke color</Panel.Label>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MenuBarColorPicker
                    value={mod.strokeColor}
                    onChange={(strokeColor) => update({ strokeColor })}
                    disabled={false}
                  />
                  <IgTextField
                    fullWidth
                    size="small"
                    value={mod.strokeColor}
                    onChange={(e) => update({ strokeColor: e.target.value })}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid size={6}>
              <Panel.TextField
                label="Stroke width"
                value={mod.strokeWidth}
                onChange={(e) => update({ strokeWidth: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* Corner radius + Padding */}
          <Grid container spacing={2}>
            <Grid size={6}>
              <Panel.TextField
                label="Corner radius"
                value={mod.cornerRadius}
                onChange={(e) => update({ cornerRadius: e.target.value })}
              />
            </Grid>
            <Grid size={6}>
              <Panel.TextField
                label="Padding"
                value={mod.padding}
                onChange={(e) => update({ padding: e.target.value })}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SquareDashedBottom size={16} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Margin + Line height + Letter spacing */}
          <Grid container spacing={2}>
            <Grid size={4}>
              <Panel.TextField
                label="Margin"
                value={mod.margin}
                onChange={(e) => update({ margin: e.target.value })}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Maximize size={16} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={4}>
              <Panel.TextField
                label="Line height"
                value={mod.lineHeight}
                onChange={(e) => update({ lineHeight: e.target.value })}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ArrowDownUp size={16} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={4}>
              <Panel.TextField
                label="Letter spacing"
                value={mod.letterSpacing}
                onChange={(e) => update({ letterSpacing: e.target.value })}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ArrowLeftRight size={16} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
      <ImageLibraryDialog id={imageLibraryDialog.id} />
      <URLInputDialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        onApply={(iconSrc) => update({ iconSrc })}
      />
    </>
  );
};
